import React from 'react'
import { Eye, MoreHorizontal } from 'lucide-react'
import { useOportunidadesFechadas } from '../hooks/useOportunidadesFechadas'
import type { OpportunityItem } from '../types'
import { useOportunidadeModal } from '../context/OportunidadeModalContext'
import { DropdownMenu } from '@/shared/ui/DropdownMenu'
import { Button } from '@/shared/ui'
import LoadingCandles from '@/shared/ui/LoadingCandles'
import ClosedOpportunitiesSearch from './ClosedOpportunitiesSearch'
import ClosedOpportunityActionSheet from './ClosedOpportunityActionSheet'
import { Select } from '@/shared/ui/Select'
import { useAuth } from '@/modules/auth/context/AuthContext'
import { useUserDateFormatter } from '@/modules/auth/hooks/useUserTimezone'
import { isMasterByEmail } from '@/shared/utils/role'
import ObjectiveIcon from './ObjectiveIcon'
import { useSignalsEnabled } from '@/modules/signals/hooks/useSignalsEnabled'
import {
  fetchSignalsOutcomesForOpportunities,
  type OpportunitySignalsOutcome,
  type OpportunitySignals,
  type SignalSide,
} from '@/modules/signals'
import { fetchSignalsClosedReasonsForOpportunities } from '@/modules/signals/services/signalsClosedReasonsService'
import { SignalCard } from '@/modules/signals/components/SignalCard'
import { useResultsEnabled, useResultsDrawer } from '@/modules/resultados'
import { useCommunityEnabled, useCommunityDrawer } from '@/modules/comunidade'
import { getClosedOpportunitySignalsColumnTimestamp } from '../utils/closedOpportunitiesSort'

const ENTRY_MISSED_REASON = 'signals_entry_missed_trend_resumed'
const CLOSED_BATCH = 10

function getSignalsResultLabel(signals?: OpportunitySignalsOutcome): string {
  const tp1 = !!signals?.tp1Hit
  const tp2 = !!signals?.tp2Hit
  const tp3 = !!signals?.tp3Hit
  const stop = !!signals?.stopHit

  if (tp1 && tp2 && tp3) {
    return 'Projeções: [1], [2] e [3] atingidas \uD83D\uDCB0\uD83D\uDCB0\uD83D\uDCB0'
  }

  if (tp1 && tp2) {
    return 'Projeções: [1] e [2] atingidas \uD83D\uDCB0\uD83D\uDCB0'
  }

  if (tp1) {
    return 'Projeção [1] atingida \uD83D\uDCB0'
  }

  if (stop) {
    return 'Atenção: Zona de Invalidação alcançada \uD83D\uDEE1\uFE0F'
  }

  return 'Resultado indefinido'
}

type ClosedActionsContextValue = {
  openActions: (item: OpportunityItem) => void
}

const ClosedActionsContext =
  React.createContext<ClosedActionsContextValue | null>(null)

function useClosedActions() {
  return React.useContext(ClosedActionsContext)
}

export default function ClosedOpportunities() {
  const { items, loading, error } = useOportunidadesFechadas(200)

  // Deduplicacao por ID (SSOT: uma oportunidade por ID)
  const uniqueItems = React.useMemo<OpportunityItem[]>(() => {
    const map = new Map<string, OpportunityItem>()
    for (const it of items) {
      const id = String(it.id)
      if (!map.has(id)) map.set(id, it)
    }
    const arr = Array.from(map.values())

    return arr.sort((a, b) => {
      const ta = a.closedAt
        ? Date.parse(a.closedAt)
        : a.publishedAt
        ? Date.parse(a.publishedAt)
        : 0
      const tb = b.closedAt
        ? Date.parse(b.closedAt)
        : b.publishedAt
        ? Date.parse(b.publishedAt)
        : 0
      return tb - ta
    })
  }, [items])

  // Filtros: objetivo (Long/Short) e tipo de oportunidade
  const [objectiveFilter, setObjectiveFilter] = React.useState<'' | 'Long' | 'Short'>('')
  const [recFilter, setRecFilter] = React.useState<string>('')

  const recOptions = React.useMemo(() => {
    const knownOrder = new Map<string, number>([
      ['Scalp Trade', 1],
      ['Scalp / Day Trade', 2],
      ['Day Trade', 3],
      ['Day / Swing Trade', 4],
      ['Swing Trade', 5],
      ['Swing / Position Trade', 6],
    ])
    const set = new Set<string>()

    for (const it of uniqueItems) {
      const rec = String(it.recommendation || '').trim()
      if (rec) set.add(rec)
    }

    return Array.from(set.values()).sort(
      (a, b) =>
        (knownOrder.get(a) || 99) - (knownOrder.get(b) || 99) ||
        a.localeCompare(b),
    )
  }, [uniqueItems])

  // Busca local (nome ou sigla)
  const [query, setQuery] = React.useState('')
  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase()
    const base = !q
      ? uniqueItems
      : uniqueItems.filter((it) => {
          const sym = String(it.symbol || '').toLowerCase()
          const name = String(it.name || '').toLowerCase()
          return sym.includes(q) || name.includes(q)
        })

    return base.filter((it) => {
      const byObjective = objectiveFilter ? it.objective === objectiveFilter : true
      const byRec = recFilter ? String(it.recommendation || '') === recFilter : true
      return byObjective && byRec
    })
  }, [query, uniqueItems, objectiveFilter, recFilter])

  const signalsEnabled = useSignalsEnabled()
  const [signalsMap, setSignalsMap] = React.useState<
    Record<string, OpportunitySignalsOutcome>
  >({})
  const [closedReasonMap, setClosedReasonMap] = React.useState<
    Record<string, string | null>
  >({})
  const [signalsLoading, setSignalsLoading] = React.useState(false)
  const communityEnabled = useCommunityEnabled()
  const { openCommunityDrawer } = useCommunityDrawer()

  // Quando o modulo de sinais estiver ativo, mostramos apenas
  // oportunidades encerradas por TP3, Stop ou anulação de entrada.
  const filteredForDisplay = React.useMemo(
    () => filtered,
    [filtered],
  )

  const sortedForDisplay = React.useMemo(() => {
    if (!signalsEnabled) return filteredForDisplay

    const arr = [...filteredForDisplay]

    arr.sort((a, b) => {
      const keyA = String(a.id)
      const keyB = String(b.id)

      const ta = getClosedOpportunitySignalsColumnTimestamp(a)
      const tb = getClosedOpportunitySignalsColumnTimestamp(b)
      if (tb !== ta) return tb - ta

      const reasonA = String(closedReasonMap[keyA] || '').toLowerCase()
      const reasonB = String(closedReasonMap[keyB] || '').toLowerCase()

      const hasSignalsClosureA =
        !!signalsMap[keyA] &&
        (reasonA === 'signals_tp3_hit' ||
          reasonA === 'signals_stop_hit' ||
          reasonA === ENTRY_MISSED_REASON)

      const hasSignalsClosureB =
        !!signalsMap[keyB] &&
        (reasonB === 'signals_tp3_hit' ||
          reasonB === 'signals_stop_hit' ||
          reasonB === ENTRY_MISSED_REASON)

      if (hasSignalsClosureA !== hasSignalsClosureB) {
        return hasSignalsClosureA ? -1 : 1
      }

      const ida = Number(a.id)
      const idb = Number(b.id)
      if (Number.isFinite(ida) && Number.isFinite(idb)) return idb - ida
      return String(b.id).localeCompare(String(a.id))
    })

    return arr
  }, [filteredForDisplay, signalsEnabled, signalsMap, closedReasonMap])

  const filteredIds = React.useMemo(
    () => filtered.map((it) => Number(it.id)).filter((id) => Number.isFinite(id)),
    [filtered],
  )
  const filteredIdsKey = React.useMemo(() => filteredIds.join(','), [filteredIds])

  React.useEffect(() => {
    if (!signalsEnabled) return
    if (!filteredIds.length) {
      setSignalsMap({})
      setClosedReasonMap({})
      return
    }

    let cancelled = false
    setSignalsLoading(true)

    Promise.all([
      fetchSignalsOutcomesForOpportunities(filteredIds),
      fetchSignalsClosedReasonsForOpportunities(filteredIds),
    ])
      .then(([signals, reasons]) => {
        if (cancelled) return

        const merged: Record<string, OpportunitySignalsOutcome> = { ...signals }
        const reasonsMap = reasons || {}

        // Ajusta o resultado de sinais de acordo com o motivo oficial (TP3 / Stop / entry missed)
        for (const it of filtered) {
          const key = String(it.id)
          const rawReason = reasonsMap[key]
          const reason = String(rawReason || '').toLowerCase()

          const isTp3Closure =
            reason === 'signals_tp3_hit' || reason === ENTRY_MISSED_REASON
          const isStopClosure = reason === 'signals_stop_hit'

          if (!isTp3Closure && !isStopClosure) continue

          const existing = merged[key]
          if (existing) {
            if (isTp3Closure) {
              existing.resultType = 'tp3'
              existing.tp3Hit = true
              if (existing.tp2 !== null) existing.tp2Hit = true
              if (existing.tp1 !== null) existing.tp1Hit = true
              existing.stopHit = false
            } else if (reason === 'signals_stop_hit') {
              existing.resultType = 'stop'
              existing.stopHit = true
            }
            merged[key] = existing
          }
        }

        setSignalsMap(merged)
        setClosedReasonMap(reasonsMap)
      })
      .catch((e) => {
        if (cancelled) return
        // eslint-disable-next-line no-console
        console.error('ClosedOpportunities signals load error', e)
        setSignalsMap({})
        setClosedReasonMap({})
      })
      .finally(() => {
        if (!cancelled) setSignalsLoading(false)
      })

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signalsEnabled, filteredIdsKey, filtered])

  const baseList = sortedForDisplay
  const [visibleCount, setVisibleCount] = React.useState(0)
  const sentinelRef = React.useRef<HTMLDivElement | null>(null)
  const [autoLoadEnabled, setAutoLoadEnabled] = React.useState(true)
  const [actionOpen, setActionOpen] = React.useState(false)
  const [actionItem, setActionItem] = React.useState<OpportunityItem | null>(null)

  const handleOpenActions = React.useCallback((item: OpportunityItem) => {
    setActionItem(item)
    setActionOpen(true)
  }, [])

  const actionsValue = React.useMemo<ClosedActionsContextValue>(
    () => ({
      openActions: handleOpenActions,
    }),
    [handleOpenActions],
  )

  React.useEffect(() => {
    const total = baseList.length
    if (!total) {
      setVisibleCount(0)
      return
    }
    setVisibleCount((current) => {
      if (!current) return Math.min(CLOSED_BATCH, total)
      if (current > total) return total
      return current
    })
  }, [baseList.length])

  const hasMoreToShow = visibleCount < baseList.length
  const visibleItems = React.useMemo(
    () => (visibleCount ? baseList.slice(0, visibleCount) : baseList),
    [baseList, visibleCount],
  )

  const handleLoadMore = React.useCallback(() => {
    const total = baseList.length
    if (!total) return
    setVisibleCount((current) => Math.min(current + CLOSED_BATCH, total))
  }, [baseList.length])

  React.useEffect(() => {
    if (!hasMoreToShow) return
    const el = sentinelRef.current
    if (!el || typeof IntersectionObserver === 'undefined') {
      setAutoLoadEnabled(false)
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            handleLoadMore()
          }
        })
      },
      { rootMargin: '160px 0px 0px 0px' },
    )

    observer.observe(el)

    return () => {
      observer.disconnect()
    }
  }, [handleLoadMore, hasMoreToShow])

  return (
    <ClosedActionsContext.Provider value={actionsValue}>
      <div className="w-full">
      <div className="mb-3">
        <div className="text-lg font-semibold">Oportunidades encerradas</div>
        <div className="mt-3 grid grid-cols-1 gap-2 lg:grid-cols-3">
          <ClosedOpportunitiesSearch value={query} onChange={setQuery} />
          <Select
            className="h-12"
            value={objectiveFilter}
            onChange={(e) =>
              setObjectiveFilter(e.target.value as '' | 'Long' | 'Short')
            }
            aria-label="Filtrar objetivo"
          >
            <option value="">Objetivo: Todos</option>
            <option value="Long">Long</option>
            <option value="Short">Short</option>
          </Select>
          <Select
            className="h-12"
            value={recFilter}
            onChange={(e) => setRecFilter(e.target.value)}
            aria-label="Filtrar tipo de oportunidade"
          >
            <option value="">Tipo: Todos</option>
            {recOptions.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </Select>
        </div>
      </div>

      {error && (
        <div className="mb-3 rounded-md border border-destructive/30 bg-destructive/10 p-2 text-sm text-destructive-foreground">
          {String(error)}
        </div>
      )}

      {loading ? (
        <div className="flex w-full items-center justify-center py-6">
          <LoadingCandles
            className="min-h-[80px]"
            label="Carregando oportunidades encerradas..."
          />
        </div>
      ) : (
        <>
          {baseList.length === 0 ? (
            <div className="text-sm text-[#434343]">
              {signalsEnabled
                ? 'Nenhuma oportunidade encerrada por sinais encontrada para os filtros atuais.'
                : 'Nenhuma oportunidade encerrada encontrada para os filtros atuais.'}
            </div>
          ) : (
            <>
              {signalsEnabled ? (
                <>
                  <div className="hidden lg:block">
                    <div className="overflow-x-auto rounded-xl border border-[#262626] bg-[#1a1a1a]">
                      <div className="grid grid-cols-[minmax(0,1.2fr),minmax(0,1fr),minmax(0,0.7fr),minmax(0,2.4fr),minmax(0,1.7fr),auto] items-center gap-4 border-b border-[#161616] px-4 py-2 text-xs font-semibold text-foreground/70">
                        <div>Ativo</div>
                        <div>Timeframes / Tipo</div>
                        <div>Objetivo</div>
                        <div>Sinais (Entrada / Alvos / Stop)</div>
                        <div>Resultado</div>
                        <div className="text-right">Ações</div>
                      </div>
                      {visibleItems.map((it) => {
                        const key = String(it.id)
                        const sig = signalsMap[key]
                        const rawReason = closedReasonMap[key]
                        const reason = String(rawReason || '').toLowerCase()
                        const isSignalsClosure =
                          reason === 'signals_tp3_hit' ||
                          reason === 'signals_stop_hit' ||
                          reason === ENTRY_MISSED_REASON

                        if (isSignalsClosure && sig) {
                          return (
                            <ClosedSignalsRow
                              key={it.id}
                              item={it}
                              signals={sig}
                            />
                          )
                        }

                        // Fechamentos antigos / sem sinais: usa layout clássico
                        return <ClosedRow key={it.id} item={it} />
                      })}
                    </div>
                  </div>

                  <div className="flex flex-col gap-3 lg:hidden">
                    {visibleItems.map((it) => {
                      const key = String(it.id)
                      const sig = signalsMap[key]
                      const rawReason = closedReasonMap[key]
                      const reason = String(rawReason || '').toLowerCase()
                      const isSignalsClosure =
                        reason === 'signals_tp3_hit' ||
                        reason === 'signals_stop_hit' ||
                        reason === ENTRY_MISSED_REASON

                      if (isSignalsClosure && sig) {
                        return (
                          <ClosedSignalsCardMobile
                            key={it.id}
                            item={it}
                            signals={sig}
                          />
                        )
                      }

                      // Fechamentos antigos / sem sinais: usa card mobile clássico
                      return <ClosedCardMobile key={it.id} item={it} />
                    })}
                  </div>
                </>
              ) : (
                <>
                  <div className="hidden lg:flex flex-col gap-3">
                    {visibleItems.map((it) => (
                      <ClosedRow key={it.id} item={it} />
                    ))}
                  </div>
                  <div className="flex flex-col gap-3 lg:hidden">
                    {visibleItems.map((it) => (
                      <ClosedCardMobile key={it.id} item={it} />
                    ))}
                  </div>
                </>
              )}

              {hasMoreToShow &&
                (autoLoadEnabled ? (
                  <div
                    ref={sentinelRef}
                    className="mt-4 flex w-full items-center justify-center py-2"
                  >
                    <LoadingCandles
                      className="min-h-[80px]"
                      label="Carregando mais encerradas..."
                    />
                  </div>
                ) : (
                  <div className="mt-4 flex w-full items-center justify-center">
                    <Button
                      variant="outline"
                      className="h-8 px-4 text-xs"
                      onClick={handleLoadMore}
                    >
                      Carregar mais encerradas
                    </Button>
                  </div>
                ))}
            </>
          )}
        </>
      )}

      <ClosedOpportunityActionSheet
        open={actionOpen}
        onOpenChange={setActionOpen}
        item={actionItem}
      />
      </div>
    </ClosedActionsContext.Provider>
  )
}

function ClosedRow({ item }: { item: OpportunityItem }) {
  const { openModal, openDebug } = useOportunidadeModal()
  const { user, profile } = useAuth()
  const isMaster = isMasterByEmail(user?.email) || profile?.role === 'master'
  const resultsEnabled = useResultsEnabled()
  const { openResultsDrawer } = useResultsDrawer()
  const canUseResults = resultsEnabled === true
  const communityEnabled = useCommunityEnabled()
  const { openCommunityDrawer } = useCommunityDrawer()
  const canUseChat = communityEnabled === true

  const handleOpenResults = React.useCallback(() => {
    if (!canUseResults) return
    const idNum = Number(item.id)
    if (!Number.isFinite(idNum)) return
    openResultsDrawer({
      opportunityId: idNum,
      symbol: item.symbol,
      name: item.name,
      logoUrl: item.logo_url,
    })
  }, [canUseResults, item.id, item.symbol, item.name, item.logo_url, openResultsDrawer])

  const handleOpenChat = React.useCallback(() => {
    if (!canUseChat) return
    const idNum = Number(item.id)
    if (!Number.isFinite(idNum)) return
    openCommunityDrawer({
      opportunityId: idNum,
      symbol: item.symbol,
      name: item.name,
      logoUrl: item.logo_url,
    })
  }, [canUseChat, item.id, item.symbol, item.name, item.logo_url, openCommunityDrawer])

  return (
    <div className="rounded-xl border border-[#161616] bg-background/40 p-4 opacity-60">
      <div className="grid items-center gap-6 md:grid-cols-[1.6fr,1.4fr,1fr,0.4fr]">
        {/* Ativo */}
        <div className="flex items-center gap-3">
          {item.logo_url ? (
            <img
              src={item.logo_url}
              alt={item.symbol}
              className="h-8 w-8 flex-shrink-0 rounded-full object-cover"
            />
          ) : (
            <div className="h-8 w-8 flex-shrink-0 rounded-full bg-muted" />
          )}
          <div className="min-w-0">
            <div className="truncate text-sm font-medium">
              {item.name || item.symbol}
            </div>
            <div className="truncate text-xs text-[#434343]">{item.symbol}</div>
          </div>
        </div>

        {/* Tipo */}
        <div>
          <div className="text-sm font-medium">{item.recommendation || '-'}</div>
        </div>

        {/* Objetivo */}
        <div className="flex items-center gap-2">
          <div
            className={[
              'text-sm font-medium',
              item.objective === 'Long'
                ? 'text-emerald-400'
                : item.objective === 'Short'
                ? 'text-red-400'
                : 'text-foreground/80',
            ].join(' ')}
          >
            {item.objective || '-'}
          </div>
          <ObjectiveIcon objective={item.objective} />
        </div>

        {/* Ação */}
        <div className="flex items-center justify-end">
          {isMaster ? (
            <DropdownMenu
              trigger={
                <button
                  type="button"
                  className="inline-flex items-center rounded p-1 hover:bg-muted/30"
                >
                  <MoreHorizontal className="h-5 w-5 text-[#434343]" />
                </button>
              }
              items={[
                {
                  label: 'Detalhes',
                  onSelect: () =>
                    openModal({
                      id: item.id,
                      symbol: item.symbol,
                      name: item.name,
                      logo_url: item.logo_url,
                      kind: 'closed',
                    }),
                },
                ...(canUseResults
                  ? [
                      {
                        label: 'Resultado',
                        onSelect: () => handleOpenResults(),
                      },
                    ]
                  : []),
                ...(canUseChat
                  ? [
                      {
                        label: 'Bate papo',
                        onSelect: () => handleOpenChat(),
                      },
                    ]
                  : []),
                {
                  label: 'Debug',
                  onSelect: () =>
                    openDebug({
                      id: item.id,
                      symbol: item.symbol,
                      name: item.name,
                      logo_url: item.logo_url,
                      kind: 'closed',
                    }),
                },
              ]}
              align="end"
            />
          ) : (
            <button
              type="button"
              onClick={() =>
                openModal({
                  id: item.id,
                  symbol: item.symbol,
                  name: item.name,
                  logo_url: item.logo_url,
                  kind: 'closed',
                })
              }
              className="inline-flex items-center rounded p-1 hover:bg-muted/30"
            >
              <Eye className="h-5 w-5 text-[#434343]" />
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

function ClosedCardMobile({ item }: { item: OpportunityItem }) {
  const { openModal } = useOportunidadeModal()
  const actions = useClosedActions()

  const objectiveLabel =
    item.objective === 'Short'
      ? 'bearish'
      : item.objective === 'Long'
      ? 'bullish'
      : item.objective || '-'

  const handleClick = React.useCallback(() => {
    if (actions?.openActions) {
      actions.openActions(item)
      return
    }

    openModal({
      id: item.id,
      symbol: item.symbol,
      name: item.name,
      logo_url: item.logo_url,
      kind: 'closed',
    })
  }, [actions, item, openModal])

  return (
    <button
      type="button"
      onClick={handleClick}
      className="rounded-xl border border-[#161616] bg-background/40 p-3 text-left hover:bg-muted/20"
    >
      <div className="relative grid grid-cols-[auto,1fr,auto] items-center gap-3">
        {item.logo_url ? (
          <img
            src={item.logo_url}
            alt={item.symbol}
            className="h-8 w-8 flex-shrink-0 rounded-full object-cover"
          />
        ) : (
          <div className="h-8 w-8 flex-shrink-0 rounded-full bg-muted" />
        )}
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold">
            {item.name || item.symbol}
          </div>
          <div className="truncate text-xs text-foreground/60">{item.symbol}</div>
        </div>
        <div className="flex items-center justify-end gap-2">
          <span
            className={[
              'text-xs font-medium',
              item.objective === 'Long'
                ? 'text-emerald-400'
                : item.objective === 'Short'
                ? 'text-red-400'
                : 'text-foreground/80',
            ].join(' ')}
          >
            {objectiveLabel}
          </span>
          <ObjectiveIcon objective={item.objective} />
        </div>
        <div className="pointer-events-none absolute left-1/2 -translate-x-1/2 max-w-[55%] truncate text-center text-[11px] font-medium text-foreground/80">
          {item.recommendation || '-'}
        </div>
      </div>
    </button>
  )
}

function ClosedSignalsRow({
  item,
  signals,
}: {
  item: OpportunityItem
  signals?: OpportunitySignalsOutcome
}) {
  const { openModal, openDebug } = useOportunidadeModal()
  const { user, profile } = useAuth()
  const isMaster = isMasterByEmail(user?.email) || profile?.role === 'master'
  const { formatUserDateTime } = useUserDateFormatter()
  const resultsEnabled = useResultsEnabled()
  const { openResultsDrawer } = useResultsDrawer()
  const canUseResults = resultsEnabled === true
  const communityEnabled = useCommunityEnabled()
  const { openCommunityDrawer } = useCommunityDrawer()
  const canUseChat = communityEnabled === true

  const publishedAtText = React.useMemo(
    () => (item.publishedAt ? formatUserDateTime(item.publishedAt) : null),
    [item.publishedAt, formatUserDateTime],
  )

  const handleOpenResults = React.useCallback(() => {
    if (!canUseResults) return
    const idNum = Number(item.id)
    if (!Number.isFinite(idNum)) return
    openResultsDrawer({
      opportunityId: idNum,
      symbol: item.symbol,
      name: item.name,
      logoUrl: item.logo_url,
    })
  }, [canUseResults, item.id, item.symbol, item.name, item.logo_url, openResultsDrawer])

  const handleOpenChat = React.useCallback(() => {
    if (!canUseChat) return
    const idNum = Number(item.id)
    if (!Number.isFinite(idNum)) return
    openCommunityDrawer({
      opportunityId: idNum,
      symbol: item.symbol,
      name: item.name,
      logoUrl: item.logo_url,
    })
  }, [canUseChat, item.id, item.symbol, item.name, item.logo_url, openCommunityDrawer])

  const resultLabel = React.useMemo(
    () => getSignalsResultLabel(signals),
    [signals],
  )

  const mergedSignals = React.useMemo<OpportunitySignals | null>(() => {
    if (!signals) return null

    const side: SignalSide =
      item.objective === 'Long'
        ? 'long'
        : item.objective === 'Short'
        ? 'short'
        : 'long'

    return {
      opportunityId: signals.opportunityId,
      side,
      tfMandante: item.fractalUpper || '',
      tfSuperior: null,
      entryMin: signals.entryMin,
      entryMax: signals.entryMax,
      entryMid: signals.entryMid,
      stopPrice: signals.stopPrice,
      stopAggressive: signals.stopPrice,
      stopConservative: signals.stopPrice,
      tp1: signals.tp1,
      tp2: signals.tp2,
      tp3: signals.tp3,
      tp1Hit: signals.tp1Hit,
      tp2Hit: signals.tp2Hit,
      tp3Hit: signals.tp3Hit,
      stopHit: signals.stopHit,
    }
  }, [item.fractalUpper, item.objective, signals])

  const objectiveLabel =
    item.objective === 'Short'
      ? 'bearish'
      : item.objective === 'Long'
      ? 'bullish'
      : item.objective || '-'

  return (
    <div className="grid grid-cols-[minmax(0,1.2fr),minmax(0,1fr),minmax(0,0.7fr),minmax(0,2.4fr),minmax(0,1.7fr),auto] items-center gap-4 border-b border-[#161616] px-4 py-3 text-sm last:border-b-0">
      {/* Ativo */}
      <div className="flex items-center gap-3">
        {item.logo_url ? (
          <img
            src={item.logo_url}
            alt={item.symbol}
            className="h-9 w-9 flex-shrink-0 rounded-full object-cover"
          />
        ) : (
          <div className="h-9 w-9 flex-shrink-0 rounded-full bg-muted" />
        )}
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold">
            {item.name || item.symbol}
          </div>
          <div className="truncate text-xs text-[#8a8a8a]">{item.symbol}</div>
        </div>
      </div>

      {/* Timeframes / Tipo */}
      <div className="min-w-0 text-[10px] sm:text-xs">
        <div className="truncate font-semibold text-foreground">
          {[item.fractalLower, item.fractalUpper].filter(Boolean).join(' | ') ||
            '-'}
        </div>
        <div className="mt-0.5 truncate text-[9px] sm:text-[10px] text-[#8a8a8a]">
          {item.recommendation || '-'}
        </div>
      </div>

      {/* Objetivo */}
      <div className="flex items-center gap-2">
        <span
          className={[
            'text-[10px] font-semibold sm:text-xs',
            item.objective === 'Long'
              ? 'text-emerald-400'
              : item.objective === 'Short'
              ? 'text-red-400'
              : 'text-foreground/80',
          ].join(' ')}
        >
          {objectiveLabel}
        </span>
        <ObjectiveIcon objective={item.objective} />
      </div>

      {/* Sinais (Entrada / Alvos / Stop) */}
      <div className="min-w-0 flex items-center text-[10px] leading-tight">
        {mergedSignals ? (
          <SignalCard signals={mergedSignals} size="compact" dateText={publishedAtText} />
        ) : (
          <span className="text-xs text-muted-foreground">Sinais indisponíveis</span>
        )}
      </div>

      {/* Resultado */}
      <div className="flex items-center text-[10px] font-semibold whitespace-normal break-words leading-tight">
        <span>{resultLabel}</span>
      </div>

      {/* Ações */}
      <div className="flex items-start justify-end">
        {isMaster ? (
          <DropdownMenu
            trigger={
              <button
                type="button"
                className="inline-flex items-center rounded p-1 hover:bg-muted/30"
              >
                <MoreHorizontal className="h-5 w-5 text-[#434343]" />
              </button>
            }
            items={[
              {
                label: 'Detalhes',
                onSelect: () =>
                  openModal({
                    id: item.id,
                    symbol: item.symbol,
                    name: item.name,
                    logo_url: item.logo_url,
                    kind: 'closed',
                  }),
              },
              ...(canUseResults
                ? [
                    {
                      label: 'Resultado',
                      onSelect: () => handleOpenResults(),
                    },
                  ]
                : []),
              ...(canUseChat
                ? [
                    {
                      label: 'Bate papo',
                      onSelect: () => handleOpenChat(),
                    },
                  ]
                : []),
              {
                label: 'Debug',
                onSelect: () =>
                  openDebug({
                    id: item.id,
                    symbol: item.symbol,
                    name: item.name,
                    logo_url: item.logo_url,
                    kind: 'closed',
                  }),
              },
            ]}
            align="end"
          />
          ) : (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() =>
                  openModal({
                    id: item.id,
                    symbol: item.symbol,
                    name: item.name,
                    logo_url: item.logo_url,
                    kind: 'closed',
                  })
                }
                className="inline-flex items-center rounded p-1 hover:bg-muted/30"
              >
                <Eye className="h-5 w-5 text-[#434343]" />
              </button>
              {canUseResults && (
                <button
                  type="button"
                  onClick={handleOpenResults}
                  className="text-[11px] font-semibold text-sky-400 hover:underline"
                >
                  Resultado
                </button>
              )}
              {canUseChat && (
                <button
                  type="button"
                  onClick={handleOpenChat}
                  className="text-[11px] font-semibold text-sky-400 hover:underline"
                >
                  Bate papo
                </button>
              )}
            </div>
          )}
      </div>
    </div>
  )
}

function ClosedSignalsCardMobile({
  item,
  signals,
}: {
  item: OpportunityItem
  signals?: OpportunitySignalsOutcome
}) {
  const { openModal } = useOportunidadeModal()
  const actions = useClosedActions()
  const { formatUserDateTime } = useUserDateFormatter()

  const publishedAtText = React.useMemo(
    () => (item.publishedAt ? formatUserDateTime(item.publishedAt) : null),
    [item.publishedAt, formatUserDateTime],
  )

  const resultLabel = React.useMemo(
    () => getSignalsResultLabel(signals),
    [signals],
  )

  const mergedSignals = React.useMemo<OpportunitySignals | null>(() => {
    if (!signals) return null

    const side: SignalSide =
      item.objective === 'Long'
        ? 'long'
        : item.objective === 'Short'
        ? 'short'
        : 'long'

    return {
      opportunityId: signals.opportunityId,
      side,
      tfMandante: item.fractalUpper || '',
      tfSuperior: null,
      entryMin: signals.entryMin,
      entryMax: signals.entryMax,
      entryMid: signals.entryMid,
      stopPrice: signals.stopPrice,
      stopAggressive: signals.stopPrice,
      stopConservative: signals.stopPrice,
      tp1: signals.tp1,
      tp2: signals.tp2,
      tp3: signals.tp3,
      tp1Hit: signals.tp1Hit,
      tp2Hit: signals.tp2Hit,
      tp3Hit: signals.tp3Hit,
      stopHit: signals.stopHit,
    }
  }, [item.fractalUpper, item.objective, signals])

  const objectiveLabel =
    item.objective === 'Short'
      ? 'bearish'
      : item.objective === 'Long'
      ? 'bullish'
      : item.objective || '-'

  return (
    <button
      type="button"
      onClick={() => {
        if (actions?.openActions) {
          actions.openActions(item)
          return
        }
        openModal({
          id: item.id,
          symbol: item.symbol,
          name: item.name,
          logo_url: item.logo_url,
          kind: 'closed',
        })
      }}
      className="rounded-xl border-[4px] border-[#262626] bg-[#1a1a1a] p-3 text-left"
    >
      <div className="flex items-center gap-3">
        {item.logo_url ? (
          <img
            src={item.logo_url}
            alt={item.symbol}
            className="h-9 w-9 flex-shrink-0 rounded-full object-cover"
          />
        ) : (
          <div className="h-9 w-9 flex-shrink-0 rounded-full bg-muted" />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <div className="truncate text-sm font-semibold">
                {item.name || item.symbol}
              </div>
              <div className="truncate text-xs text-foreground/60">
                {item.symbol}
              </div>
            </div>
            <span className="text-xs font-semibold text-foreground">
              {resultLabel}
            </span>
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
            <span className="text-foreground">
              {[item.fractalLower, item.fractalUpper]
                .filter(Boolean)
                .join(' | ') || '-'}
            </span>
            <span
              className={[
                'font-semibold',
                item.objective === 'Long'
                  ? 'text-emerald-400'
                  : item.objective === 'Short'
                  ? 'text-red-400'
                  : 'text-foreground/80',
              ].join(' ')}
            >
              {objectiveLabel}
            </span>
          </div>
          <div className="mt-0.5 text-[11px] text-[#8a8a8a]">
            {item.recommendation || '-'}
          </div>
        </div>
      </div>

      <div className="mt-2 border-t border-[#161616]" />

      <div className="mt-2">
        {mergedSignals ? (
          <SignalCard signals={mergedSignals} size="compact" dateText={publishedAtText} />
        ) : (
          <span className="text-[11px] text-muted-foreground">
            Sinais indisponíveis
          </span>
        )}
      </div>
    </button>
  )
}

